import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faEnvelope,
  faKey,
  faSave,
  faSpinner,
  faCheckCircle,
  faExclamationTriangle,
  faShieldAlt,
  faUserEdit,
  faLock,
  faEye,
  faEyeSlash,
  faGlobe,
  faCog,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../contexts/ThemeContext';
import PageHeader from '../components/common/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage, languages } from '../contexts/LanguageContext';
import userService from '../services/userService';

const UserProfile = () => {
  const { darkMode } = useTheme();
  const { user, setUser } = useAuth();
  const { language, setLanguage, availableLanguages, t } = useLanguage();

  const [activeTab, setActiveTab] = useState('profile'); // profile, security, preferences
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Profile form state
  const [profileData, setProfileData] = useState({
    displayName: '',
    email: '',
    username: ''
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Load user data
  useEffect(() => {
    if (user) {
      setProfileData({
        displayName: user.displayName || '',
        email: user.email || '',
        username: user.username || ''
      });
    }
  }, [user]);

  // Handle profile update
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await userService.updateProfile(user._id, {
        displayName: profileData.displayName,
        email: profileData.email
      });

      // Update local user state
      setUser(prev => ({
        ...prev,
        displayName: profileData.displayName,
        email: profileData.email
      }));

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update profile'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Validate passwords
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      setLoading(false);
      return;
    }

    try {
      await userService.changePassword(user._id, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to change password'
      });
    } finally {
      setLoading(false);
    }
  };

  // Password strength indicator
  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, text: '', color: '' };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) return { level: strength, text: 'Weak', color: 'red' };
    if (strength <= 3) return { level: strength, text: 'Fair', color: 'yellow' };
    if (strength <= 4) return { level: strength, text: 'Good', color: 'blue' };
    return { level: strength, text: 'Strong', color: 'green' };
  };

  const passwordStrength = getPasswordStrength(passwordData.newPassword);

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      red_team: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      blue_team: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      purple_team: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900 dark:text-fuchsia-200'
    };
    return colors[role] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  };

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title="User Profile"
        subtitle="Manage your account settings and security"
      />

      {/* User Card */}
      <div className={`rounded-lg shadow mb-6 p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="flex items-center">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold ${
            darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'
          }`}>
            {profileData.displayName?.charAt(0)?.toUpperCase() || profileData.username?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div className="ml-6">
            <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {profileData.displayName || profileData.username}
            </h2>
            <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              @{profileData.username}
            </p>
            <div className="flex items-center mt-2 space-x-2">
              <span className={`px-2 py-1 text-xs rounded-full ${getRoleBadgeColor(user?.role)}`}>
                {user?.role?.replace('_', ' ').toUpperCase()}
              </span>
              {user?.team && (
                <span className={`px-2 py-1 text-xs rounded-full ${
                  darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                }`}>
                  {user.team.name || 'Team'}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={`border-b mb-6 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex space-x-6">
          <button
            onClick={() => setActiveTab('profile')}
            className={`pb-3 px-1 font-medium transition-colors ${
              activeTab === 'profile'
                ? `border-b-2 ${darkMode ? 'border-blue-500 text-blue-400' : 'border-blue-500 text-blue-600'}`
                : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FontAwesomeIcon icon={faUserEdit} className="mr-2" />
            Profile
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`pb-3 px-1 font-medium transition-colors ${
              activeTab === 'security'
                ? `border-b-2 ${darkMode ? 'border-blue-500 text-blue-400' : 'border-blue-500 text-blue-600'}`
                : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FontAwesomeIcon icon={faLock} className="mr-2" />
            {t('profile.security') || 'Security'}
          </button>
          <button
            onClick={() => setActiveTab('preferences')}
            className={`pb-3 px-1 font-medium transition-colors ${
              activeTab === 'preferences'
                ? `border-b-2 ${darkMode ? 'border-blue-500 text-blue-400' : 'border-blue-500 text-blue-600'}`
                : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FontAwesomeIcon icon={faCog} className="mr-2" />
            {t('profile.preferences') || 'Preferences'}
          </button>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg flex items-center ${
          message.type === 'success'
            ? (darkMode ? 'bg-green-900/50 text-green-200' : 'bg-green-100 text-green-800')
            : (darkMode ? 'bg-red-900/50 text-red-200' : 'bg-red-100 text-red-800')
        }`}>
          <FontAwesomeIcon
            icon={message.type === 'success' ? faCheckCircle : faExclamationTriangle}
            className="mr-2"
          />
          {message.text}
        </div>
      )}

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Profile Information
          </h3>
          <form onSubmit={handleProfileUpdate}>
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FontAwesomeIcon icon={faUser} className="mr-2" />
                  Username
                </label>
                <input
                  type="text"
                  value={profileData.username}
                  disabled
                  className={`w-full px-4 py-2 rounded border ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                />
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Username cannot be changed
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FontAwesomeIcon icon={faUser} className="mr-2" />
                  Display Name
                </label>
                <input
                  type="text"
                  value={profileData.displayName}
                  onChange={(e) => setProfileData({ ...profileData, displayName: e.target.value })}
                  className={`w-full px-4 py-2 rounded border ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                  }`}
                  placeholder="Your display name"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                  Email Address
                </label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  className={`w-full px-4 py-2 rounded border ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                  }`}
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className={`px-6 py-2 rounded font-medium ${
                    darkMode
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {loading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Change Password
          </h3>
          <form onSubmit={handlePasswordChange}>
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FontAwesomeIcon icon={faKey} className="mr-2" />
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    className={`w-full px-4 py-2 pr-10 rounded border ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                    }`}
                    placeholder="Enter current password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                  >
                    <FontAwesomeIcon icon={showPasswords.current ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FontAwesomeIcon icon={faKey} className="mr-2" />
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    className={`w-full px-4 py-2 pr-10 rounded border ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                    }`}
                    placeholder="Enter new password"
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                  >
                    <FontAwesomeIcon icon={showPasswords.new ? faEyeSlash : faEye} />
                  </button>
                </div>
                {passwordData.newPassword && (
                  <div className="mt-2">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded">
                        <div
                          className={`h-2 rounded transition-all ${
                            passwordStrength.color === 'red' ? 'bg-red-500' :
                            passwordStrength.color === 'yellow' ? 'bg-yellow-500' :
                            passwordStrength.color === 'blue' ? 'bg-blue-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: `${(passwordStrength.level / 5) * 100}%` }}
                        />
                      </div>
                      <span className={`text-xs ${
                        passwordStrength.color === 'red' ? 'text-red-500' :
                        passwordStrength.color === 'yellow' ? 'text-yellow-500' :
                        passwordStrength.color === 'blue' ? 'text-blue-500' :
                        'text-green-500'
                      }`}>
                        {passwordStrength.text}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FontAwesomeIcon icon={faKey} className="mr-2" />
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    className={`w-full px-4 py-2 pr-10 rounded border ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                    } ${
                      passwordData.confirmPassword && passwordData.confirmPassword !== passwordData.newPassword
                        ? 'border-red-500'
                        : ''
                    }`}
                    placeholder="Confirm new password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                  >
                    <FontAwesomeIcon icon={showPasswords.confirm ? faEyeSlash : faEye} />
                  </button>
                </div>
                {passwordData.confirmPassword && passwordData.confirmPassword !== passwordData.newPassword && (
                  <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
                )}
              </div>

              <div className={`p-4 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Password Requirements:
                </h4>
                <ul className={`text-xs space-y-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <li className={passwordData.newPassword.length >= 8 ? 'text-green-500' : ''}>
                    • At least 8 characters
                  </li>
                  <li className={/[A-Z]/.test(passwordData.newPassword) && /[a-z]/.test(passwordData.newPassword) ? 'text-green-500' : ''}>
                    • Mix of uppercase and lowercase letters
                  </li>
                  <li className={/\d/.test(passwordData.newPassword) ? 'text-green-500' : ''}>
                    • At least one number
                  </li>
                  <li className={/[^a-zA-Z0-9]/.test(passwordData.newPassword) ? 'text-green-500' : ''}>
                    • At least one special character
                  </li>
                </ul>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading || passwordData.newPassword !== passwordData.confirmPassword}
                  className={`px-6 py-2 rounded font-medium ${
                    darkMode
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {loading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      {t('profile.changingPassword') || 'Changing Password...'}
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faKey} className="mr-2" />
                      {t('profile.changePassword') || 'Change Password'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Preferences Tab */}
      {activeTab === 'preferences' && (
        <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {t('profile.languagePreference') || 'Language Preference'}
          </h3>

          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon icon={faGlobe} className="mr-2" />
                {t('profile.selectLanguage') || 'Select Language'}
              </label>
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('profile.languageDescription') || 'Choose your preferred language for the application interface.'}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {availableLanguages.filter(lang => languages[lang]).map((langCode) => {
                  const lang = languages[langCode];
                  const isSelected = language === langCode;

                  return (
                    <button
                      key={langCode}
                      onClick={() => {
                        setLanguage(langCode);
                        setMessage({ type: 'success', text: t('profile.languageChanged') || 'Language changed successfully!' });
                        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
                      }}
                      className={`p-4 rounded-lg border-2 transition-all text-left flex items-center justify-between ${
                        isSelected
                          ? darkMode
                            ? 'border-blue-500 bg-blue-900/30'
                            : 'border-blue-500 bg-blue-50'
                          : darkMode
                          ? 'border-gray-600 hover:border-gray-500 bg-gray-700'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div>
                        <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {lang.nativeName}
                        </div>
                        <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {lang.name}
                        </div>
                      </div>
                      {isSelected && (
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          darkMode ? 'bg-blue-500' : 'bg-blue-500'
                        }`}>
                          <FontAwesomeIcon icon={faCheck} className="text-white text-sm" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={`mt-6 p-4 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <FontAwesomeIcon icon={faGlobe} className="mr-2" />
                {t('profile.languageNote') || 'Your language preference is saved locally in your browser and will be remembered for future sessions.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
